import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStudioModuleText } from '@/i18n/studioModuleText';
import { IPE_UPLOAD_ACCEPT, IPE_UPLOAD_FORMAT_HINT_WITH_DIMENSION } from '@/utils/ipeImageUploadFormats';
import { uploadStudioSourceFiles } from '@/utils/imageUrlUtils';
import { toAbsoluteViewPath, warmDisplayCacheFromRecords } from '@/utils/imageDisplayAccess';
import { useIpeAuth } from '@/context/IpeAuthContext';
import StudioRefSourceImage from '@/pages/ipe/workbench/components/StudioRefSourceImage';
import StudioGenerateCostLabel, { useStudioGenerateBtnHasCost } from '@/pages/ipe/workbench/components/StudioGenerateCostLabel';
import { ImagePlus, Loader2, Plus, Scissors } from 'lucide-react';
import LibraryModalContent from '@/pages/ipe/workbench/modals/LibraryModalContent';
import MaterialRecommendationPickerModal from '@/pages/ipe/workbench/components/MaterialRecommendationPickerModal';
import AiInspirationPickerModal from '@/pages/ipe/workbench/components/AiInspirationPickerModal';
import { mapGalleryItemsWithAiInspirationIds } from '@/pages/ipe/workbench/utils/studioGalleryPickHelpers';
import AiStudioTaskHistoryPanel from '@/pages/ipe/workbench/components/AiStudioTaskHistoryPanel';
import { useWorkbenchHistoryPanelRestore } from '@/pages/ipe/workbench/hooks/useWorkbenchHistoryPanelRestore';
import WorkbenchStudioSidebarHead from '@/pages/ipe/workbench/components/WorkbenchStudioSidebarHead';
import AiStudioResultGallery from '@/pages/ipe/workbench/components/AiStudioResultGallery';
import AiStudioSourceSelectAll from '@/pages/ipe/workbench/components/AiStudioSourceSelectAll';
import {
  getImageCropStudioConfig,
  IMAGE_CROP_BATCH_LIMIT_TOAST,
  IMAGE_CROP_MAX_SOURCES,
  IMAGE_CROP_NAV_ID,
} from '../constants/imageCropConfig';
import {
  canAddStudioSources,
  clampStudioSources,
  formatStudioSourceCountMeta,
  formatStudioSourceLimitHint,
  studioSourceLimitToast,
  assertStudioSourceBatch,
} from '@/pages/ipe/workbench/utils/studioSourceLimit';
import { resolveImg2ImgReferenceForApi } from '@/pages/ipe/workbench/utils/img2imgPublicUrl';
import {
  mapStudioTaskReferencePreviews,
  resolveStudioTaskPreviewUrl,
} from '@/pages/ipe/workbench/constants/fundingTaskImageUrlFields';
import {
  validateImg2ImgFile,
  loadImageDimensions,
  validateImg2ImgDimensions,
} from '@/pages/ipe/workbench/utils/img2imgReference';
import { resolveAuthDisplayName } from '@/pages/ipe/workbench/utils/resolveAuthDisplayName';
import { allocateTaskIdentifiersLocal } from '@/pages/ipe/workbench/utils/taskIdGenerator';
import {
  buildAiStudioImageSuccessResult,
  completeStudioImageTaskUpdate,
  createAiStudioImageTask,
  markAiStudioImageTaskFailed,
} from '@/pages/ipe/workbench/utils/aiStudioImageTaskRecord';
import { runStudioImageCropBatch } from '../services/imageCropStudioBatch';
import { persistImageCropStudioOutputs } from '../services/imageCropStudioPersist';
import { fetchCropStudioGalleryDisplayUrls } from '../utils/imageCropListPreview';
import { prefetchStudioDisplayUrls } from '@/pages/ipe/workbench/utils/studioImagePreview';
import {
  presentImageCropApiError,
  resolveImageCropFailedMessage,
} from '../utils/imageCropTaskApiError';
import ImageCropStudioProgressBar from '../components/ImageCropStudioProgressBar';
import {
  createCropStudioProgress,
  createIdleStudioProgress,
  createUploadStudioProgress,
  patchStudioProgress,
} from '../utils/imageCropStudioProgress';
import {
  isImageCropTaskCancelled,
  registerImageCropCreateAbort,
  unregisterImageCropCreateAbort,
} from '../utils/imageCropTaskCancel';
import StudioFolderFileInput from '@/components/ipe/StudioFolderFileInput';
import useStudioFolderUpload from '@/hooks/useStudioFolderUpload';
import { importIpeSourcesToImageSpace } from '@/pages/ipe/workbench/utils/ipeDetailSourcesPrefill';
import '@/styles/ipe/workbench/Img2ImgPage.css';
import '@/styles/ipe/workbench/ImageFissionPage.css';
import '@/image-crop/styles/ImageCropStudioPage.css';

function makeLocalId() {
  return `crop-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function warmStudioSourceDisplayCache(items) {
  warmDisplayCacheFromRecords(
    (items || []).map((item) => ({
      ...item,
      viewUrl: item.previewUrl || item.viewUrl,
      displayUrl: item.previewUrl || item.displayUrl,
    })),
    (item) => item.url,
  );
}

function resolveStudioUploadPreviewFallback(previewUrl, uploadResult) {
  if (previewUrl) return previewUrl;
  const viewUrl = uploadResult?.viewUrl || uploadResult?.storedViewUrl;
  if (!viewUrl) return '';
  return toAbsoluteViewPath(viewUrl);
}

function resolveStudioUploadFailureMessage(lastReason) {
  const reason = String(lastReason || '').trim();
  if (!reason) return '图片上传失败，请稍后重试';
  if (/登录|未授权|UNAUTHORIZED|LOGIN_REQUIRED/i.test(reason)) {
    return '请先登录后再上传图片';
  }
  if (/Network Error|Failed to fetch|网络|连接|timeout|超时|ECONNABORTED/i.test(reason)) {
    return '无法连接服务器，请确认后端已启动且端口与 client/.env 一致';
  }
  return reason;
}

export default function ImageCropStudioPage({
  showToast: showToastProp,
  sharedImageSpaceImages = [],
  onMergeImageSpaceItems,
}) {
  const { t, title, field } = useStudioModuleText('imageCrop');
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useIpeAuth();
  const operatorName = useMemo(() => resolveAuthDisplayName(user), [user]);
  const studioConfig = useMemo(() => getImageCropStudioConfig(), []);
  const appliedSourcesPrefillRef = useRef(false);
  const appliedFromDetailRef = useRef(false);
  const fileRef = useRef(null);
  const uploadZoneRef = useRef(null);
  const uploadDropdownRef = useRef(null);
  const dragCounterRef = useRef(0);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [localSources, setLocalSources] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const maxSources = IMAGE_CROP_MAX_SOURCES;
  const sourceLimitToast = studioSourceLimitToast(maxSources) || IMAGE_CROP_BATCH_LIMIT_TOAST;
  const canAddMoreSources = canAddStudioSources(maxSources, localSources.length);
  const { isHistoryOpen, setIsHistoryOpen, historyRestore, clearHistoryRestore } = useWorkbenchHistoryPanelRestore(IMAGE_CROP_NAV_ID);
  const [isUploadDropdownOpen, setIsUploadDropdownOpen] = useState(false);
  const [isLibraryModalOpen, setIsLibraryModalOpen] = useState(false);
  const [isMaterialPickerOpen, setIsMaterialPickerOpen] = useState(false);
  const [isAiInspirationPickerOpen, setIsAiInspirationPickerOpen] = useState(false);
  const [librarySelectionDraft, setLibrarySelectionDraft] = useState([]);
  const [outputUrls, setOutputUrls] = useState([]);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [studioProgress, setStudioProgress] = useState(createIdleStudioProgress);

  const toast = useCallback((message, type = 'success') => {
    if (typeof showToastProp === 'function') showToastProp(message, type);
  }, [showToastProp]);

  const selectedCount = selectedIds.length;

  const hasGenerateCost = useStudioGenerateBtnHasCost(IMAGE_CROP_NAV_ID);

  const onFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) {
      toast(t('studioToast.pickFile'), 'warning');
      return;
    }
    const batch = assertStudioSourceBatch(files, maxSources, localSources.length);
    if (!batch.ok) {
      toast(batch.message, 'warning');
      return;
    }
    const toProcess = [];
    for (const file of batch.items) {
      const err = validateImg2ImgFile(file);
      if (err) {
        toast(err, 'warning');
        continue;
      }
      toProcess.push(file);
    }
    if (toProcess.length === 0) return;

    setUploading(true);
    const uploadBaseline = localSources.length;
    setStudioProgress(createUploadStudioProgress(uploadBaseline, uploadBaseline + toProcess.length));
    try {
      let lastFailureReason = '';
      let previews = [];
      try {
        previews = await uploadStudioSourceFiles(toProcess, { source: IMAGE_CROP_NAV_ID });
      } catch (uploadError) {
        lastFailureReason = uploadError?.message || '图片上传失败';
        toast(lastFailureReason, 'error');
        previews = [];
      }

      const added = [];
      for (let i = 0; i < toProcess.length; i += 1) {
        const file = toProcess[i];
        const {
          storedUrl,
          previewUrl,
          imageSpaceId,
          uploadResult,
        } = previews[i] || {};
        if (!storedUrl) {
          lastFailureReason = '服务器未返回图片地址';
          continue;
        }
        try {
          const effectivePreviewUrl = resolveStudioUploadPreviewFallback(previewUrl, uploadResult)
            || toAbsoluteViewPath(uploadResult?.viewUrl || uploadResult?.storedViewUrl || '')
            || previewUrl
            || '';
          const uploadViewUrl = String(
            uploadResult?.viewUrl
            || uploadResult?.storedViewUrl
            || uploadResult?.displayUrl
            || '',
          ).trim();
          const dims = effectivePreviewUrl
            ? await loadImageDimensions(effectivePreviewUrl)
            : null;
          const width = dims?.width;
          const height = dims?.height;
          const dErr = validateImg2ImgDimensions(width, height);
          if (dErr) {
            lastFailureReason = dErr;
            toast(dErr, 'warning');
            continue;
          }
          const id = imageSpaceId ? String(imageSpaceId) : makeLocalId();
          added.push({
            id,
            name: file?.name || id,
            url: storedUrl,
            previewUrl: effectivePreviewUrl || previewUrl,
            viewUrl: uploadViewUrl || effectivePreviewUrl || previewUrl,
            displayUrl: uploadViewUrl || effectivePreviewUrl || previewUrl,
            imageSpaceId: imageSpaceId ? String(imageSpaceId) : null,
          });
          setStudioProgress((prev) => patchStudioProgress(prev, {
            uploadDone: uploadBaseline + added.length,
            uploadTotal: Math.max(prev.uploadTotal || 0, uploadBaseline + toProcess.length),
          }));
          if (typeof onMergeImageSpaceItems === 'function' && imageSpaceId) {
            onMergeImageSpaceItems([{
              id: String(imageSpaceId),
              name: file?.name || id,
              url: storedUrl,
              source: IMAGE_CROP_NAV_ID,
              size: file?.size ?? null,
              width,
              height,
              createdAt: new Date().toISOString().slice(0, 16).replace('T', ' '),
            }]);
          }
        } catch (uploadError) {
          lastFailureReason = uploadError?.message || '图片上传失败';
        }
      }
      if (added.length === 0) {
        toast(resolveStudioUploadFailureMessage(lastFailureReason), 'warning');
        return;
      }
      setLocalSources((prev) => [...prev, ...added]);
      warmStudioSourceDisplayCache(added);
      setSelectedIds((prev) => {
        const set = new Set(prev);
        added.forEach((a) => set.add(String(a.id)));
        return [...set];
      });
      toast(t('studioToast.added', { count: added.length, unit: t('studioToast.unitImage', { count: added.length }) }), 'success');
    } catch (e) {
      toast(e.message || t('studioToast.readFailed'), 'error');
    } finally {
      setUploading(false);
      setStudioProgress(createIdleStudioProgress());
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [localSources.length, maxSources, onMergeImageSpaceItems, sourceLimitToast, toast]);

  const {
    folderInputRef,
    openFolderPicker,
    handleFolderInputChange,
    ingestDataTransfer,
  } = useStudioFolderUpload({
    onFiles,
    toast,
    disabled: uploading || submitting,
  });

  useEffect(() => {
    if (localSources.length === 0) return;
    warmStudioSourceDisplayCache(localSources);
  }, [localSources]);

  useEffect(() => {
    const onPaste = (event) => {
      if (submitting || uploading) return;
      const items = event.clipboardData?.items;
      if (!items) return;
      const files = [];
      for (const item of items) {
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }
      if (files.length > 0) {
        event.preventDefault();
        onFiles(files);
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [submitting, uploading, onFiles]);

  useEffect(() => {
    if (!isUploadDropdownOpen) return undefined;
    const onPointerDown = (event) => {
      if (uploadDropdownRef.current && !uploadDropdownRef.current.contains(event.target)) {
        setIsUploadDropdownOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [isUploadDropdownOpen]);

  useEffect(() => {
    const sources = location.state?.cropSources;
    if (
      !location.state?.openCreate
      || !Array.isArray(sources)
      || sources.length === 0
      || appliedSourcesPrefillRef.current
    ) {
      return undefined;
    }
    appliedSourcesPrefillRef.current = true;
    void (async () => {
      try {
        const { ids, urlMap, rows } = await importIpeSourcesToImageSpace(sources, {
          sourceTag: IMAGE_CROP_NAV_ID,
        });
        if (ids.length === 0) {
          toast(t('studioToast.upstreamLoadFail'), 'warning');
          return;
        }
        if (typeof onMergeImageSpaceItems === 'function' && rows.length > 0) {
          onMergeImageSpaceItems(rows);
        }
        const added = ids.map((id) => ({
          id: String(id),
          name: `上游_${id}`,
          url: urlMap[id] || urlMap[String(id)] || '',
        })).filter((x) => x.url);
        if (added.length === 0) {
          toast(t('studioToast.upstreamUrlFail'), 'warning');
          return;
        }
        setLocalSources((prev) => clampStudioSources([...prev, ...added], maxSources));
        setSelectedIds(added.map((a) => String(a.id)));
        toast(t('studioToast.prefilled', { count: added.length, unit: t('studioToast.unitImage', { count: added.length }) }), 'success');
      } catch (e) {
        console.error('[图案裁剪] 上游结果预填失败', e);
        toast(t('studioToast.bringFail'), 'error');
      } finally {
        const rest = { ...(location.state || {}) };
        delete rest.openCreate;
        delete rest.cropSources;
        navigate(location.pathname, { replace: true, state: Object.keys(rest).length ? rest : {} });
      }
    })();
    return undefined;
  }, [location.state, location.pathname, navigate, toast, onMergeImageSpaceItems]);

  useEffect(() => {
    const imageIds = location.state?.imageIds;
    if (!imageIds?.length || appliedFromDetailRef.current || appliedSourcesPrefillRef.current) {
      return undefined;
    }
    if (!sharedImageSpaceImages?.length) return undefined;
    appliedFromDetailRef.current = true;
    const added = [];
    imageIds.forEach((imageId) => {
      const img = sharedImageSpaceImages.find((x) => String(x.id) === String(imageId));
      if (!img) return;
      const url = img.url || img.dataUrl || '';
      if (!String(url).trim()) return;
      added.push({
        id: String(imageId),
        name: img.name || String(imageId),
        url: String(url).trim(),
      });
    });
    if (added.length > 0) {
      setLocalSources((prev) => clampStudioSources([...prev, ...added], maxSources));
      setSelectedIds(added.map((a) => a.id));
      toast(t('studioToast.preselected', { count: added.length, unit: t('studioToast.unitImage', { count: added.length }) }), 'success');
    } else {
      toast(t('studioToast.notInSpacePick'), 'info');
    }
    navigate(location.pathname, { replace: true, state: {} });
    return undefined;
  }, [location.state, location.pathname, sharedImageSpaceImages, navigate, toast]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const key = String(id);
      if (prev.includes(key)) return prev.filter((x) => x !== key);
      return [...prev, key];
    });
  };

  const handleSelectAllChange = useCallback((checked) => {
    setSelectedIds(checked ? localSources.map((item) => String(item.id)) : []);
  }, [localSources]);

  const removeLocal = (id) => {
    setLocalSources((prev) => prev.filter((x) => x.id !== id));
    setSelectedIds((prev) => prev.filter((x) => x !== String(id)));
  };

  const clearAllLocal = () => {
    setLocalSources([]);
    setSelectedIds([]);
    setOutputUrls([]);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleSubmit = async () => {
    if (selectedCount === 0) {
      toast(t('studioToast.uploadSelectFirst', { unit: t('studioToast.unitImage', { count: 1 }) }), 'warning');
      return;
    }

    const sources = localSources.filter((item) => selectedIds.includes(String(item.id)));
    const sourcesSnapshot = sources.map((item) => ({ ...item }));
    const imageIds = sourcesSnapshot.map((item) => item.id);
    const estimatedTotal = sourcesSnapshot.length;
    setSubmitting(true);
    setStudioProgress(createCropStudioProgress({
      total: estimatedTotal,
      uploadDone: localSources.length,
    }));
    let taskId;
    let apiSucceeded = false;
    const initialResult = {
      referencePreviews: mapStudioTaskReferencePreviews(sourcesSnapshot),
      previewUrl: resolveStudioTaskPreviewUrl(sourcesSnapshot),
      operator: operatorName,
      operatorName,
      engine: 'qwen-detect',
    };

    try {
      const imageUrls = [];
      for (const item of sourcesSnapshot) {
        const resolved = await resolveImg2ImgReferenceForApi(item.url);
        if (!resolved) {
          throw new Error(`图片「${item.name}」无法解析，请重新上传`);
        }
        imageUrls.push(resolved);
      }

      const { taskId: allocatedId, subAccount } = allocateTaskIdentifiersLocal(IMAGE_CROP_NAV_ID);
      taskId = allocatedId;
      await createAiStudioImageTask({
        taskType: IMAGE_CROP_NAV_ID,
        taskName: '图案裁剪',
        taskId,
        subAccount,
        imageIds,
        previewUrl: initialResult.previewUrl,
        initialResult: {
          ...initialResult,
          referenceUrls: imageUrls,
        },
        estimatedTotal,
      });
      setHistoryRefreshKey((k) => k + 1);

      const abortController = new AbortController();
      registerImageCropCreateAbort(taskId, abortController);

      let collectedUrls = [];
      let outputs = [];
      let failedCount = 0;

      try {
        ({ collectedUrls, outputs, failedCount } = await runStudioImageCropBatch({
          taskId,
          items: sourcesSnapshot.map((item, index) => ({
            imageId: item.id,
            imageUrl: imageUrls[index],
          })),
          shouldAbort: () => abortController.signal.aborted || isImageCropTaskCancelled(taskId),
          showToast: toast,
          onProgress: ({
            detectBatches,
            detectBatchIndex,
            successCount,
            failedCount: failedSoFar,
            total,
          }) => {
            setStudioProgress((prev) => patchStudioProgress(prev, {
              phase: 'crop',
              uploadDone: localSources.length,
              detectBatches,
              detectBatchIndex,
              cropSuccess: successCount,
              cropFailed: failedSoFar,
              cropTotal: total,
            }));
          },
        }));
      } finally {
        unregisterImageCropCreateAbort(taskId);
      }

      if (collectedUrls.length === 0) {
        throw new Error('裁剪完成但未产出有效图片');
      }

      apiSucceeded = true;
      setOutputUrls(collectedUrls);
      const successResult = buildAiStudioImageSuccessResult({
        ...initialResult,
        referenceUrls: imageUrls,
        outputs,
      }, { engine: 'qwen-detect', outputs, urls: collectedUrls }, collectedUrls);

      const status = failedCount > 0 ? 'partial' : 'success';
      const finalResult = await completeStudioImageTaskUpdate({
        taskId,
        status,
        successCount: collectedUrls.length,
        failedCount,
        successResult,
        navId: IMAGE_CROP_NAV_ID,
        onMergeImageSpaceItems,
      });

      await persistImageCropStudioOutputs({
        taskId,
        sources: sourcesSnapshot,
        imageUrls,
        outputs,
      });
      const galleryUrls = await fetchCropStudioGalleryDisplayUrls({
        taskId,
        sources: sourcesSnapshot,
        finalResult,
        fallbackUrls: collectedUrls,
      });
      await prefetchStudioDisplayUrls(galleryUrls);
      setOutputUrls(galleryUrls);
      setHistoryRefreshKey((k) => k + 1);

      toast(
        failedCount > 0
          ? `已裁剪 ${collectedUrls.length} 张（${failedCount} 张失败），结果已展示在右侧`
          : `裁剪成功，共 ${collectedUrls.length} 张，已展示在右侧，可在历史记录查看`,
        failedCount > 0 ? 'warning' : 'success',
      );
    } catch (e) {
      if (taskId && !apiSucceeded) {
        try {
          await markAiStudioImageTaskFailed(taskId, initialResult, estimatedTotal, e);
          setHistoryRefreshKey((k) => k + 1);
        } catch {
          /* ignore */
        }
      }
      const presented = presentImageCropApiError(e, { showToast: toast });
      const failedMessage = resolveImageCropFailedMessage(presented, '图案裁剪失败');
      if (!presented?.imageCropError?.toast) {
        toast(failedMessage, 'error');
      }
    } finally {
      setSubmitting(false);
      setStudioProgress(createIdleStudioProgress());
    }
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    dragCounterRef.current += 1;
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setIsDragOver(false);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragOver(false);
    if (submitting || uploading) return;
    void ingestDataTransfer(event.dataTransfer);
  };

  const openUploadMenu = () => setIsUploadDropdownOpen((open) => !open);
  const triggerFileSelect = () => {
    fileRef.current?.click();
    setIsUploadDropdownOpen(false);
  };
  const triggerFolderSelect = () => {
    setIsUploadDropdownOpen(false);
    void openFolderPicker();
  };

  const handleUploadInputChange = (e) => {
    onFiles(e.target.files);
    setIsUploadDropdownOpen(false);
  };

  const openLibraryFromUpload = () => {
    setLibrarySelectionDraft([]);
    setIsLibraryModalOpen(true);
    setIsUploadDropdownOpen(false);
  };

  const openMaterialPickerFromUpload = () => {
    setIsMaterialPickerOpen(true);
    setIsUploadDropdownOpen(false);
  };

  const openAiInspirationPickerFromUpload = () => {
    setIsAiInspirationPickerOpen(true);
    setIsUploadDropdownOpen(false);
  };

  const addSourcesFromMaterialRecommendation = (selectedItems, galleryOptions = {}) => {
    const {
      sourceLabel = '素材推荐',
      closeMaterialPicker = true,
      closeInspirationPicker = false,
    } = galleryOptions;
    if (!canAddStudioSources(maxSources, localSources.length)) {
      toast(sourceLimitToast, 'warning');
      return;
    }
    const existingIds = new Set(localSources.map((s) => String(s.id)));
    const candidates = [];
    (selectedItems || []).forEach((item) => {
      const materialId = item?.id ? String(item.id) : '';
      if (!materialId || existingIds.has(materialId)) return;
      const url = item.url || item.src || '';
      if (!String(url).trim()) return;
      candidates.push({
        id: materialId,
        name: item.title || item.name || materialId,
        url: String(url).trim(),
      });
    });
    const batch = assertStudioSourceBatch(candidates, maxSources, localSources.length);
    if (!batch.ok) {
      toast(batch.message, 'warning');
      return;
    }
    const added = batch.items;
    if (added.length === 0) {
      toast(t('studioToast.invalidOrAdded'), 'warning');
      return;
    }
    setLocalSources((prev) => [...prev, ...added]);
    setSelectedIds((prev) => {
      const set = new Set(prev);
      added.forEach((a) => set.add(String(a.id)));
      return [...set];
    });
    toast(t('studioToast.addedFrom', { source: sourceLabel, count: added.length, unit: t('studioToast.unitImage', { count: added.length }) }), 'success');
    if (closeMaterialPicker) setIsMaterialPickerOpen(false);
    if (closeInspirationPicker) setIsAiInspirationPickerOpen(false);
  };

  const addSourcesFromAiInspiration = (selectedItems) => {
    addSourcesFromMaterialRecommendation(
      mapGalleryItemsWithAiInspirationIds(selectedItems),
      { sourceLabel: 'AI 灵感', closeMaterialPicker: false, closeInspirationPicker: true },
    );
  };

  const handleLibrarySelectionToggle = (imageId) => {
    const id = String(imageId);
    setLibrarySelectionDraft((prev) => {
      const has = prev.some((x) => String(x) === id);
      if (has) return prev.filter((x) => String(x) !== id);
      const existingIds = new Set(localSources.map((s) => String(s.id)));
      const total = localSources.length + prev.filter((x) => !existingIds.has(String(x))).length;
      if (!canAddStudioSources(maxSources, total)) {
        toast(sourceLimitToast, 'warning');
        return prev;
      }
      return [...prev, id];
    });
  };

  const handleLibraryModalClose = () => {
    setLibrarySelectionDraft([]);
    setIsLibraryModalOpen(false);
  };

  const addSourcesFromLibrary = (imageIds) => {
    if (!canAddStudioSources(maxSources, localSources.length)) {
      toast(sourceLimitToast, 'warning');
      return;
    }
    const existingIds = new Set(localSources.map((s) => String(s.id)));
    const candidates = [];
    (imageIds || []).forEach((imageId) => {
      const id = String(imageId);
      if (existingIds.has(id)) return;
      const img = sharedImageSpaceImages.find((x) => String(x.id) === id);
      if (!img) return;
      const url = img.url || img.dataUrl || '';
      if (!String(url).trim()) return;
      candidates.push({ id, name: img.name || id, url: String(url).trim() });
    });
    const batch = assertStudioSourceBatch(candidates, maxSources, localSources.length);
    if (!batch.ok) {
      toast(batch.message, 'warning');
      return;
    }
    const added = batch.items;
    if (added.length === 0) {
      toast(t('studioToast.notInSpace'), 'warning');
      return;
    }
    setLocalSources((prev) => [...prev, ...added]);
    setSelectedIds((prev) => {
      const set = new Set(prev);
      added.forEach((a) => set.add(String(a.id)));
      return [...set];
    });
    toast(t('studioToast.added', { count: added.length, unit: t('studioToast.unitImage', { count: added.length }) }), 'success');
  };

  const applyTryExample = (example) => {
    if (!example?.sampleUrl || !canAddMoreSources) return;
    const trimmed = String(example.sampleUrl).trim();
    const id = `example-${example.id}`;
    if (localSources.some((s) => s.id === id)) return;
    setLocalSources((prev) => [...prev, { id, name: '案例图片', url: trimmed }]);
    setSelectedIds((prev) => [...prev, id]);
  };

  const renderUploadMenu = (secondary = false) => (
    <div
      ref={uploadDropdownRef}
      className={`img2img-studio__upload-dropdown${isUploadDropdownOpen ? ' is-open' : ''}`}
    >
      <button
        type="button"
        className={`img2img-studio__upload-btn${secondary ? ' img2img-studio__upload-btn--secondary' : ''}`}
        onClick={openUploadMenu}
        disabled={uploading || submitting}
      >
        {uploading ? (
          <>
            <Loader2 size={16} className="img2img-studio__spin" aria-hidden="true" />
            上传中…
          </>
        ) : (
          <>
            <ImagePlus size={16} aria-hidden="true" />
            {secondary ? '继续上传' : '上传图片'}
          </>
        )}
      </button>
      {isUploadDropdownOpen && (
        <div className="img2img-studio__upload-menu">
          <button type="button" onClick={triggerFileSelect} disabled={uploading || submitting}>
            选择文件
          </button>
          <button type="button" onClick={triggerFolderSelect} disabled={uploading || submitting}>
            选择文件夹
          </button>
          <button type="button" onClick={openMaterialPickerFromUpload} disabled={submitting}>
            从素材推荐选择
          </button>
          <button type="button" onClick={openAiInspirationPickerFromUpload} disabled={submitting}>
            从 AI 灵感选择
          </button>
          <button type="button" onClick={openLibraryFromUpload} disabled={submitting}>
            {t('studioChrome.fromSpace')}
          </button>
        </div>
      )}
    </div>
  );

  const hasSources = localSources.length > 0;

  return (
    <>
      <div className="img2img-page ai-outpaint-page img-fission-page image-crop-studio-page">
        <aside className="img2img-studio__sidebar" aria-label={t('studioChrome.cropSettingsAria')}>
          <WorkbenchStudioSidebarHead
            title={title || studioConfig.title}
            navId={IMAGE_CROP_NAV_ID}
            manualPlacement="headerButton"
            onHistoryClick={() => setIsHistoryOpen(true)}
            historyActive={isHistoryOpen}
          />

          <div className="img2img-studio__sidebar-body">
            {hasSources && (
              <section className="img2img-studio__section">
                <p className="image-crop-studio__status">
                  {t('studioChrome.cropAdded', { added: localSources.length, selected: selectedCount })}
                </p>
              </section>
            )}

          </div>

          <footer className="img2img-studio__sidebar-foot">
            {(studioProgress.active || submitting || uploading) && (
              <ImageCropStudioProgressBar progress={studioProgress} />
            )}
            <button
              type="button"
              className={`img2img-studio__generate-btn${!submitting && hasGenerateCost ? ' has-cost' : ''}`}
              onClick={handleSubmit}
              disabled={submitting || uploading || selectedCount === 0}
            >
              {submitting ? (
                <span className="img2img-studio__generate-main">
                  <Loader2 size={18} className="img2img-studio__spin" aria-hidden="true" />
                  {field('generating', '裁剪中…')}
                </span>
              ) : (
                <span className="img2img-studio__generate-main">
                  <Scissors size={18} aria-hidden="true" />
                  {field('generate', '立即裁剪')}
                </span>
              )}
              {!submitting ? (
                <StudioGenerateCostLabel navId={IMAGE_CROP_NAV_ID} imageCount={selectedCount} />
              ) : null}
            </button>
          </footer>
        </aside>

        <main
          className={`img2img-studio__workspace${isDragOver ? ' is-dragover' : ''}${hasSources ? ' has-refs' : ''}`}
          ref={uploadZoneRef}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          aria-label={t('studioChrome.sourceUploadAria')}
        >
          <input
            ref={fileRef}
            type="file"
            accept={IPE_UPLOAD_ACCEPT}
            multiple
            onChange={handleUploadInputChange}
            className="img2img-studio__file-input"
            disabled={uploading || submitting}
          />
          <StudioFolderFileInput
            inputRef={folderInputRef}
            onChange={handleFolderInputChange}
            disabled={uploading || submitting}
          />

          {!hasSources ? (
            <div className="img2img-studio__empty">
              <div className={`img2img-studio__showcase${isDragOver ? ' is-dragover' : ''}`}>
                <div className="img2img-studio__hero">
                  <div className="img2img-studio__hero-frame">
                    <img src={studioConfig.hero.image} alt={title || studioConfig.title} />
                  </div>
                  <div className="img2img-studio__hero-caption-wrap">
                    <p className="img2img-studio__hero-caption">{field('caption', studioConfig.hero.caption)}</p>
                  </div>
                </div>

                <div className="img2img-studio__showcase-actions">
                  {renderUploadMenu()}
                </div>

                <div className="img2img-studio__upload-hint">
                  <p>{formatStudioSourceLimitHint(maxSources)}</p>
                  <p>{IPE_UPLOAD_FORMAT_HINT_WITH_DIMENSION}</p>
                  <p>{t('studioChrome.pickAction', { action: field('action', '裁剪') })}</p>
                </div>
              </div>

              <div className="img2img-studio__try">
                <p className="img2img-studio__try-label">{t('studioChrome.tryCases')}</p>
                <div className="img2img-studio__try-grid">
                  {studioConfig.tryExamples.map((example) => (
                    <button
                      key={example.id}
                      type="button"
                      className="img2img-studio__try-item"
                      onClick={() => applyTryExample(example)}
                      title={t('studioChrome.addExampleTitle')}
                    >
                      <img src={example.thumb} alt="" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="img2img-studio__filled">
              <div className="img2img-studio__filled-card">
                <div className="img2img-studio__filled-head">
                  <div>
                    <h2 className="img2img-studio__filled-title">{t('studioChrome.sourceImage')}</h2>
                    <p className="img2img-studio__filled-meta">
                      {formatStudioSourceCountMeta(selectedCount, localSources.length, maxSources)}
                    </p>
                    <AiStudioSourceSelectAll
                      total={localSources.length}
                      selectedCount={selectedCount}
                      disabled={submitting}
                      onChange={handleSelectAllChange}
                    />
                  </div>
                  <button
                    type="button"
                    className="img2img-studio__clear-btn"
                    onClick={clearAllLocal}
                    disabled={submitting}
                  >
                    {t('studioChrome.clearAll')}
                  </button>
                </div>

                <div className="img2img-ref-grid img2img-studio__ref-grid" aria-label={t('studioChrome.pickedSources')}>
                  {localSources.map((item) => {
                    const selected = selectedIds.includes(String(item.id));
                    return (
                      <div
                        key={item.id}
                        className={`img2img-ref-thumb is-selectable${selected ? ' is-active' : ''}`}
                      >
                        <button
                          type="button"
                          className="img-fission-page__thumb-select"
                          onClick={() => toggleSelect(item.id)}
                          disabled={submitting}
                          aria-pressed={selected}
                          aria-label={selected ? t('studioChrome.deselectItem', { name: item.name }) : t('studioChrome.selectItem', { name: item.name })}
                        >
                          <StudioRefSourceImage item={item} />
                          {selected && <span className="img2img-ref-badge">{t('studioChrome.selectedBadge')}</span>}
                        </button>
                        <button
                          type="button"
                          className="img2img-ref-remove"
                          onClick={() => removeLocal(item.id)}
                          aria-label={t('studioChrome.remove')}
                          disabled={submitting}
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                  {canAddMoreSources && (
                    <button
                      type="button"
                      className="img2img-studio__add-tile"
                      onClick={triggerFileSelect}
                      disabled={uploading || submitting}
                      aria-label={t('studioChrome.continueUpload')}
                    >
                      <Plus size={22} aria-hidden="true" />
                      <span>{t('studioChrome.addImage')}</span>
                    </button>
                  )}
                </div>

                {canAddMoreSources && (
                  <div className="img2img-studio__filled-actions">
                    {renderUploadMenu(true)}
                  </div>
                )}
              </div>

              <AiStudioResultGallery
                title={field('resultTitle', '裁剪结果')}
                urls={outputUrls}
                onClear={() => setOutputUrls([])}
                clearDisabled={submitting}
                altPrefix={field('resultAlt', '图案裁剪结果')}
              />
            </div>
          )}

          {isDragOver && (
            <div className="img2img-studio__drag-overlay" aria-hidden="true">
              <ImagePlus size={28} />
              <span>{t('studioChrome.dropRelease')}</span>
            </div>
          )}
        </main>

        <AiStudioTaskHistoryPanel
          navId={IMAGE_CROP_NAV_ID}
          open={isHistoryOpen}
          onClose={() => setIsHistoryOpen(false)}
          showToast={toast}
          operatorName={operatorName}
          refreshKey={historyRefreshKey}
          initialRestore={historyRestore}
          onRestoreApplied={clearHistoryRestore}
        />

        <MaterialRecommendationPickerModal
          isOpen={isMaterialPickerOpen}
          onClose={() => setIsMaterialPickerOpen(false)}
          onConfirm={addSourcesFromMaterialRecommendation}
          showToast={toast}
        />

        <AiInspirationPickerModal
          isOpen={isAiInspirationPickerOpen}
          onClose={() => setIsAiInspirationPickerOpen(false)}
          onConfirm={addSourcesFromAiInspiration}
          showToast={toast}
        />

        <LibraryModalContent
          isOpen={isLibraryModalOpen}
          onClose={handleLibraryModalClose}
          images={sharedImageSpaceImages}
          selection={librarySelectionDraft}
          onToggle={handleLibrarySelectionToggle}
          maxSelection={maxSources}
          outsideSelectedIds={localSources.map((s) => s.id)}
          onSelectionLimitReached={() => toast(sourceLimitToast, 'warning')}
          onConfirm={(positionArray) => {
            const ids = (positionArray || []).filter((x) => x != null && x !== '');
            addSourcesFromLibrary(ids);
            handleLibraryModalClose();
          }}
          selectedImageTemplates={[]}
          onUploadComplete={onMergeImageSpaceItems}
          uploadOriginSource={IMAGE_CROP_NAV_ID}
          showToast={showToastProp}
          deferUsageBump
        />
      </div>
    </>
  );
}
